import React, { useEffect, useMemo, useState } from "react";
import { db, storage } from "../firebase";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

const EMPTY_FORM = {
  id: "",
  name: "",
  templateType: "shopping",
  active: true,
  description: "",
  imageUrl: "",
  textColor: "#ffffff",
  titleBackground: "#2d1d16cc",
  titlePosition: "top",
};

const TEMPLATE_TYPES = [
  { value: "shopping", label: "Shopping Complex" },
  { value: "course", label: "University" },
  { value: "service", label: "Services" },
  { value: "all", label: "All store types" },
];

function PreviewCard({ template, storeName = "Your Store Name" }) {
  const overlayClasses =
    template.titlePosition === "bottom"
      ? "bottom-4 left-4 right-4"
      : template.titlePosition === "center"
        ? "inset-x-4 top-1/2 -translate-y-1/2"
        : "left-4 right-4 top-4";

  return (
    <div className="overflow-hidden rounded-[28px] border border-white/70 bg-[#20161d] shadow-[0_18px_45px_-20px_rgba(0,0,0,0.35)]">
      <div className="relative h-56 bg-slate-900">
        {template.imageUrl ? (
          <img
            src={template.imageUrl}
            alt={template.name || "Template preview"}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-[#29253d] via-[#3b5ca8] to-[#111827]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/10" />
        <div className={`absolute ${overlayClasses}`}>
          <div
            className="rounded-[18px] px-4 py-3 text-center text-2xl font-black shadow-lg backdrop-blur"
            style={{
              color: template.textColor || "#ffffff",
              background: template.titleBackground || "#2d1d16cc",
            }}
          >
            {storeName}
          </div>
        </div>
      </div>
      <div className="bg-[#221821] px-4 py-4 text-white">
        <div className="text-sm uppercase tracking-[0.22em] text-white/60">
          {TEMPLATE_TYPES.find((entry) => entry.value === template.templateType)?.label ||
            "Storefront"}
        </div>
        <div className="mt-2 text-lg font-bold">{template.name || "Template name"}</div>
        <div className="mt-1 text-sm text-white/70">
          {template.description || "Storefront preview with live name overlay."}
        </div>
      </div>
    </div>
  );
}

export default function StorefrontTemplates() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploadFile, setUploadFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const q = query(collection(db, "storefront_templates"), orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snap) => {
        setRows(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
      },
      (err) => {
        console.error("storefront template fetch failed", err);
        setRows([]);
      },
    );
  }, []);

  const activeCount = useMemo(
    () => rows.filter((row) => row.active !== false).length,
    [rows],
  );

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Please enter a template name.");
      return;
    }

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const refDoc = form.id
        ? doc(db, "storefront_templates", form.id)
        : doc(collection(db, "storefront_templates"));
      let imageUrl = form.imageUrl.trim();

      if (uploadFile) {
        const ext = (uploadFile.name || "").split(".").pop() || "png";
        const storageRef = ref(
          storage,
          `storefront_templates/${refDoc.id}/template.${ext}`,
        );
        await uploadBytes(storageRef, uploadFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      await setDoc(
        refDoc,
        {
          name: form.name.trim(),
          templateType: form.templateType,
          active: !!form.active,
          description: form.description.trim(),
          imageUrl,
          textColor: form.textColor || "#ffffff",
          titleBackground: form.titleBackground || "#2d1d16cc",
          titlePosition: form.titlePosition || "top",
          updatedAt: serverTimestamp(),
          ...(form.id ? {} : { createdAt: serverTimestamp() }),
        },
        { merge: true },
      );

      setMessage(form.id ? "Template updated." : "Template created.");
      setForm(EMPTY_FORM);
      setUploadFile(null);
    } catch (err) {
      console.error(err);
      setError("Failed to save storefront template.");
    } finally {
      setSaving(false);
    }
  }

  function startEdit(row) {
    setForm({
      id: row.id,
      name: row.name || "",
      templateType: row.templateType || "shopping",
      active: row.active !== false,
      description: row.description || "",
      imageUrl: row.imageUrl || "",
      textColor: row.textColor || "#ffffff",
      titleBackground: row.titleBackground || "#2d1d16cc",
      titlePosition: row.titlePosition || "top",
    });
    setUploadFile(null);
    setMessage("");
    setError("");
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Storefront Templates</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Upload the storefront frames instructors can choose from while creating
            their shop. These templates drive the public shopping-complex cards too.
          </p>
        </div>
        <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <div className="font-semibold">{activeCount} active templates</div>
          <div className="mt-1 text-blue-700/80">
            Shopping, university, and services templates can be managed here.
          </div>
        </div>
      </div>

      {(message || error) && (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            error
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          {error || message}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[0.95fr,1.05fr]">
        <form onSubmit={handleSave} className="space-y-4 rounded-3xl border bg-white p-6 shadow-sm">
          <div className="text-xl font-bold text-slate-900">
            {form.id ? "Edit template" : "Create template"}
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              Template name
            </label>
            <input
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-2xl border px-4 py-3"
              placeholder="Luxury storefront style"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Store type
              </label>
              <select
                value={form.templateType}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, templateType: e.target.value }))
                }
                className="w-full rounded-2xl border px-4 py-3"
              >
                {TEMPLATE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Title position
              </label>
              <select
                value={form.titlePosition}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, titlePosition: e.target.value }))
                }
                className="w-full rounded-2xl border px-4 py-3"
              >
                <option value="top">Top</option>
                <option value="center">Center</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              Description
            </label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              className="w-full rounded-2xl border px-4 py-3"
              placeholder="Optional note shown to instructors during preview."
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Title text color
              </label>
              <input
                type="color"
                value={form.textColor}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, textColor: e.target.value }))
                }
                className="h-12 w-full rounded-2xl border p-2"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Title background
              </label>
              <input
                type="color"
                value={form.titleBackground.slice(0, 7)}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    titleBackground: `${e.target.value}cc`,
                  }))
                }
                className="h-12 w-full rounded-2xl border p-2"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              Upload template image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full rounded-2xl border px-4 py-3"
            />
            <div className="mt-2 text-xs text-slate-500">
              If you don’t upload a new file, the existing template image URL will stay in use.
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              Existing image URL
            </label>
            <input
              value={form.imageUrl}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, imageUrl: e.target.value }))
              }
              className="w-full rounded-2xl border px-4 py-3"
              placeholder="https://..."
            />
          </div>

          <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, active: e.target.checked }))
              }
            />
            Active and available to instructors
          </label>

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white disabled:opacity-60"
            >
              {saving ? "Saving..." : form.id ? "Update Template" : "Create Template"}
            </button>
            {form.id && (
              <button
                type="button"
                onClick={() => {
                  setForm(EMPTY_FORM);
                  setUploadFile(null);
                }}
                className="rounded-2xl border px-5 py-3 font-semibold"
              >
                Cancel edit
              </button>
            )}
          </div>
        </form>

        <div className="space-y-6">
          <div className="rounded-3xl border bg-white p-6 shadow-sm">
            <div className="mb-4 text-xl font-bold text-slate-900">Live preview</div>
            <PreviewCard template={form} />
          </div>

          <div className="rounded-3xl border bg-white p-6 shadow-sm">
            <div className="mb-4 text-xl font-bold text-slate-900">Available templates</div>
            {rows.length === 0 ? (
              <div className="rounded-2xl border border-dashed p-6 text-sm text-slate-500">
                No storefront templates yet.
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {rows.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => startEdit(row)}
                    className="text-left"
                  >
                    <PreviewCard template={row} storeName="Sample Shop" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
